import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type { DailyMode, SessionKind, SessionTemplate, Settings, TrainingProgram, WorkoutExerciseResult, WorkoutRecord } from '../types'
import { displayDate, localDateKey } from '../utils/date'
import { WorkoutFlow } from '../workouts/WorkoutFlow'
import { saveWorkout } from '../storage/db'
import { buildSessionForMode, dailyModeLabels, recommendDailyMode } from '../workouts/adaptation'
import { RangeInput } from '../components/RangeInput'
import { ExerciseMedia } from '../components/ExerciseMedia'

interface TodayScreenProps {
  program: TrainingProgram
  day: number
  settings?: Settings
  todayWorkout?: WorkoutRecord
  workouts: WorkoutRecord[]
  results: WorkoutExerciseResult[]
  onChanged: () => void
  onWorkoutActive: (active: boolean) => void
  onExport: () => void
  onBackup: () => void
}

const kindLabel: Record<SessionKind, string> = {
  A: 'Базовая сила', B: 'Баланс движений', C: 'Солдатский круг', MIN: 'Короткая разминка', REST: 'Отдых', CHECK: 'Техническая проверка', RECOVERY: 'Восстановление'
}

const signalTitles: Record<DailyMode, string> = {
  rest: 'Полный отдых',
  light: 'Вернуть лёгкость',
  standard: 'Техника и запас',
  boosted: 'Добавить немного'
}

const modeOrder: DailyMode[] = ['light', 'standard', 'boosted', 'rest']
const weekdayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

function formatPlan(item: { sets: number[]; unit: string; label?: string }) {
  if (item.label) return item.label
  const value = item.sets.join(' + ')
  return item.unit === 'seconds' ? `${value} сек` : value
}

function libraryCategory(program: TrainingProgram, exerciseId: string) {
  return program.exerciseLibrary.find((item) => item.id === exerciseId)?.category
}

function movementCount(count: number) {
  const mod10 = count % 10
  const mod100 = count % 100
  const word = mod10 === 1 && mod100 !== 11 ? 'движение' : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? 'движения' : 'движений'
  return `${count} ${word}`
}

export function TodayScreen({ program, day, settings, todayWorkout, workouts, results, onChanged, onWorkoutActive, onExport, onBackup }: TodayScreenProps) {
  const scheduleDay = program.schedule.find((item) => item.day === day) || program.schedule[0]
  const requestedSession = program.sessions.find((item) => item.id === scheduleDay.sessionId)
  const completedPriorSessions = workouts.filter((item) => item.programId === program.id && item.day < day && !item.skipped && item.actualSession !== 'REST').length
  let plannedSession = requestedSession
  const visited = new Set<string>()
  while (plannedSession?.unlock && completedPriorSessions < plannedSession.unlock.minCompletedPriorSessions && !visited.has(plannedSession.id)) {
    visited.add(plannedSession.id)
    plannedSession = program.sessions.find((item) => item.id === plannedSession?.unlock?.fallbackSessionId)
  }

  const progressHeld = Boolean(requestedSession && plannedSession && requestedSession.id !== plannedSession.id)
  const [workout, setWorkout] = useState<{ session: SessionTemplate; original: SessionKind; mode: Exclude<DailyMode, 'rest'>; reason: string } | null>(null)
  const [energyToday, setEnergyToday] = useState(6)
  const [kneeToday, setKneeToday] = useState(0)
  const [heavy, setHeavy] = useState(false)
  const [modeTouched, setModeTouched] = useState(false)
  const [savingRest, setSavingRest] = useState(false)
  const [restError, setRestError] = useState('')
  const restSaveGuard = useRef(false)
  const exerciseNames = useMemo(() => new Map(program.exerciseLibrary.map((item) => [item.id, item.name])), [program.exerciseLibrary])

  const recommendation = useMemo(
    () => recommendDailyMode(scheduleDay.kind, { energy: energyToday, knee: kneeToday, heavyDay: heavy }, workouts, results),
    [energyToday, heavy, kneeToday, results, scheduleDay.kind, workouts]
  )
  const [selectedMode, setSelectedMode] = useState<DailyMode>(recommendation.mode)
  useEffect(() => { if (!modeTouched) setSelectedMode(recommendation.mode) }, [modeTouched, recommendation.mode])
  const selectedSession = useMemo(
    () => selectedMode === 'rest' ? undefined : buildSessionForMode(selectedMode, plannedSession, program),
    [plannedSession, program, selectedMode]
  )
  const modeMinutes = useMemo<Record<DailyMode, string>>(() => ({
    rest: '0 мин',
    light: `${buildSessionForMode('light', plannedSession, program).durationMin} мин`,
    standard: `${buildSessionForMode('standard', plannedSession, program).durationMin} мин`,
    boosted: `${buildSessionForMode('boosted', plannedSession, program).durationMin} мин`
  }), [plannedSession, program])

  const openWorkout = (session: SessionTemplate, mode: Exclude<DailyMode, 'rest'>, original = scheduleDay.kind) => {
    setWorkout({ session, original, mode, reason: recommendation.reason })
    onWorkoutActive(true)
  }
  const closeWorkout = () => {
    setWorkout(null)
    onWorkoutActive(false)
  }
  const start = () => {
    if (selectedMode === 'rest') return markRest()
    if (selectedSession) openWorkout(selectedSession, selectedMode)
  }
  const markRest = async () => {
    if (restSaveGuard.current) return
    restSaveGuard.current = true
    setSavingRest(true)
    setRestError('')
    try {
      const date = localDateKey()
      const now = new Date().toISOString()
      await saveWorkout({
        id: `rest:${program.id}:${date}`, date, day, programId: program.id, programVersion: program.version,
        sessionType: 'REST', originalSession: scheduleDay.kind, actualSession: 'REST', dailyMode: 'rest', recommendationReason: recommendation.reason,
        modified: scheduleDay.kind !== 'REST', minimal: false, skipped: false, durationMin: 0, startedAt: now, completedAt: now, notes: ''
      }, [])
      onChanged()
    } catch (error) {
      console.error(error)
      restSaveGuard.current = false
      setRestError('Не удалось записать отдых. Попробуй ещё раз — история не изменена.')
    } finally { setSavingRest(false) }
  }

  const completedCycle = day === program.durationDays && workouts.some((item) => item.day === program.durationDays)
  const weekNumber = Math.ceil(day / 7)
  const weekStart = (weekNumber - 1) * 7 + 1
  const weekWorkouts = workouts.filter((item) => item.day >= weekStart && item.day <= weekStart + 6 && !item.skipped && item.actualSession !== 'REST')
  const weekPlanned = program.schedule.filter((item) => item.day >= weekStart && item.day <= weekStart + 6 && item.kind !== 'REST').length
  const weekKnees = weekWorkouts.filter((item) => item.kneeAfter != null)
  const weekPullups = results.filter((item) => item.exerciseId === 'pull_up' && weekWorkouts.some((workoutItem) => workoutItem.id === item.workoutId))
  const resourceScore = Math.max(10, Math.min(100, Math.round(energyToday * 10 - kneeToday * 3 - (heavy ? 12 : 0))))
  const featuredPlan = selectedSession?.exercises.find((item) => libraryCategory(program, item.exerciseId) === 'strength') || selectedSession?.exercises[0]
  const featuredExercise = featuredPlan ? program.exerciseLibrary.find((item) => item.id === featuredPlan.exerciseId) : undefined
  const weekDays = program.schedule.filter((item) => item.week === weekNumber).slice(0, 7)
  const completedDays = new Set(workouts.filter((item) => !item.skipped).map((item) => item.day))
  const weekDone = weekDays.filter((item) => completedDays.has(item.day) && item.kind !== 'REST').length

  return (
    <div className="screen today-screen">
      <header className="today-topbar">
        <div><span>{displayDate()}</span><strong>Доброе утро</strong></div>
        <span className="today-cycle">День {day}<small>из {program.durationDays}</small></span>
      </header>

      <section className={todayWorkout ? 'today-signal is-complete' : 'today-signal'}>
        <div className="resource-ring" style={{ '--resource': `${todayWorkout ? 100 : resourceScore}%` } as CSSProperties}>
          <div><strong>{todayWorkout ? '✓' : resourceScore}</strong><span>{todayWorkout ? 'готово' : 'ресурс'}</span></div>
        </div>
        <div className="today-signal__copy">
          <p className="eyebrow">{todayWorkout ? 'Результат сохранён' : 'Рекомендация на сегодня'}</p>
          <h1>{todayWorkout ? (todayWorkout.actualSession === 'REST' ? 'Отдых принят' : 'На сегодня всё') : signalTitles[selectedMode]}</h1>
          <p>{todayWorkout
            ? todayWorkout.actualSession === 'REST'
              ? 'Восстановление — часть программы. Завтра ничего компенсировать не нужно.'
              : `${kindLabel[todayWorkout.actualSession]} · ${todayWorkout.durationMin} мин · сложность ${todayWorkout.overallDifficulty || '—'} из 5.`
            : recommendation.reason}</p>
        </div>
      </section>

      {!todayWorkout && <>
        <div className="mode-picker" role="radiogroup" aria-label="Нагрузка на день">
          {modeOrder.map((mode) => <button key={mode} role="radio" aria-checked={selectedMode === mode} className={selectedMode === mode ? 'mode-option is-selected' : 'mode-option'} onClick={() => { setSelectedMode(mode); setModeTouched(true) }}>
            <strong>{dailyModeLabels[mode].title}</strong>
            <small>{modeMinutes[mode]}</small>
            {recommendation.mode === mode && <span>Совет</span>}
          </button>)}
        </div>
        <button className="today-primary" disabled={savingRest} onClick={start}><span>{savingRest ? 'Сохраняю…' : selectedMode === 'rest' ? 'Записать отдых' : `Начать · ${selectedSession?.durationMin || '—'} мин`}</span><b aria-hidden="true">→</b></button>
        {restError && <div className="notice notice--error" role="alert">{restError}</div>}
        <details className="readiness-details readiness-details--standalone"><summary>Уточнить самочувствие</summary><div>
          <RangeInput label="Энергия сейчас" value={energyToday} onChange={setEnergyToday} leftLabel="нет сил" rightLabel="много сил" />
          <RangeInput label="Правое колено сейчас" value={kneeToday} onChange={setKneeToday} leftLabel="ничего" rightLabel="сильная боль" />
          {kneeToday >= 5 && selectedMode !== 'rest' && <div className="notice notice--warning">Не выполняй движения через боль. Безопаснее выбрать отдых.</div>}
          <label className="toggle-row"><span><strong>Тяжёлый физический день</strong><small>Учтём рабочую нагрузку</small></span><input type="checkbox" checked={heavy} onChange={(event) => setHeavy(event.target.checked)} /></label>
          {modeTouched && selectedMode !== recommendation.mode && <button className="text-action reset-mode" onClick={() => { setModeTouched(false); setSelectedMode(recommendation.mode) }}>Вернуть рекомендацию <span>{dailyModeLabels[recommendation.mode].title}</span></button>}
        </div></details>
      </>}

      {!todayWorkout && selectedSession && <section className="today-plan-card">
        <div className="section-heading"><div><p className="eyebrow">Выбранный план</p><h2>{selectedSession.name}</h2></div><span className="duration-chip">{selectedSession.durationMin} мин</span></div>
        {featuredExercise && <figure className="today-plan-media">
          <ExerciseMedia exercise={featuredExercise} compact />
          <figcaption><small>Ключевое движение</small><strong>{featuredExercise.name}</strong></figcaption>
        </figure>}
        <ol className="plan-list">{selectedSession.exercises.slice(0, 4).map((item, index) => <li key={item.exerciseId}><span><i>{String(index + 1).padStart(2, '0')}</i>{exerciseNames.get(item.exerciseId)}</span><strong>{formatPlan(item)}</strong></li>)}</ol>
        {selectedSession.exercises.length > 4 && <p className="plan-more">Ещё {movementCount(selectedSession.exercises.length - 4)} — покажем по одному во время занятия.</p>}
        {progressHeld && <div className="notice notice--progress">Объём пока не увеличен: сначала закрепляем фактически выполненные тренировки.</div>}
      </section>}

      {completedCycle && <section className="cycle-card">
        <p className="eyebrow">6 недель завершены</p>
        <h2>Текущий цикл завершён.</h2>
        <p>Экспортируй результаты и используй их для составления следующего цикла.</p>
        <div className="week-summary__grid cycle-stats">
          <span><small>Выполнено</small><strong>{workouts.filter((item) => !item.skipped).length}</strong></span>
          <span><small>Сокращено</small><strong>{workouts.filter((item) => item.minimal).length}</strong></span>
          <span><small>Пропущено</small><strong>{workouts.filter((item) => item.skipped).length}</strong></span>
          <span><small>Энергия после</small><strong>{workouts.filter((item) => item.energyAfter != null).length ? (workouts.reduce((sum, item) => sum + (item.energyAfter || 0), 0) / workouts.filter((item) => item.energyAfter != null).length).toFixed(1) : '—'}</strong></span>
        </div>
        <button className="button button--primary button--wide" onClick={onExport}>Экспортировать для анализа</button>
        <button className="button button--secondary button--wide" onClick={onBackup}>Создать полный backup</button>
      </section>}

      <section className="today-week">
        <div className="section-heading"><div><p className="eyebrow">Ритм недели</p><h2>Нагрузка распределена</h2></div><span>{weekDone} выполнено</span></div>
        <div className="today-week__days">{weekDays.map((item) => {
          const isDone = completedDays.has(item.day)
          const isToday = item.day === day
          const startWeekday = new Date(`${program.startDate}T12:00:00`).getDay()
          return <span key={item.day} className={`${isDone ? 'is-done ' : ''}${isToday ? 'is-today' : ''}`}><i>{isDone ? '✓' : item.kind === 'REST' ? '–' : ''}</i><b>{weekdayNames[(startWeekday + item.day + 5) % 7]}</b></span>
        })}</div>
      </section>

      {day % 7 === 0 && !completedCycle && <section className="week-summary">
        <p className="eyebrow">Неделя {weekNumber} завершена</p>
        <div className="week-summary__grid">
          <span><small>Запланировано</small><strong>{weekPlanned}</strong></span>
          <span><small>Выполнено</small><strong>{weekWorkouts.length}</strong></span>
          <span><small>Минимальных</small><strong>{weekWorkouts.filter((item) => item.minimal).length}</strong></span>
          <span><small>Средняя тренировка</small><strong>{weekWorkouts.length ? Math.round(weekWorkouts.reduce((sum, item) => sum + item.durationMin, 0) / weekWorkouts.length) : 0} мин</strong></span>
          <span><small>Подтягивания</small><strong>{weekPullups.length ? weekPullups.map((item) => item.actualSets.join('+')).join(' → ') : '—'}</strong></span>
          <span><small>Колено после</small><strong>{weekKnees.length ? (weekKnees.reduce((sum, item) => sum + (item.kneeAfter || 0), 0) / weekKnees.length).toFixed(1) : '—'} / 10</strong></span>
        </div>
      </section>}

      <footer className="principle">{program.principle}</footer>

      {workout && <WorkoutFlow program={program} day={day} session={workout.session} originalSession={workout.original} dailyMode={workout.mode} recommendationReason={workout.reason} initialEnergy={energyToday} initialKnee={kneeToday} settings={settings} onClose={closeWorkout} onSaved={onChanged} />}
    </div>
  )
}
